/**
 * SCREENS THAT DO NOT EXIST YET, DRAWN HONESTLY.
 *
 * Every one of these is taken from the master plan's own module spec, so what a
 * customer reacts to maps onto something already designed rather than onto a
 * shape somebody invented for a demo.
 *
 * NOTHING IN THIS DIRECTORY MAY IMPORT THE API CLIENT, and
 * `test/preview-isolation.spec.ts` enforces it. A sketch that physically cannot
 * fetch cannot accidentally display a real number next to an invented one —
 * which is the failure mode that makes mock-ups dangerous. The numbers below
 * are deliberately round and deliberately implausible as a real day's trading.
 */

export interface SketchColumn {
  readonly label: string;
  readonly align?: "end";
}

export interface Sketch {
  /** Plain Czech — these are not translated, because they are not product yet. */
  readonly title: string;
  /** What the module will do, in one sentence a restaurateur would use. */
  readonly blurb: string;
  /** The master plan's own module number, so the sketch is traceable. */
  readonly module: string;
  /** Headline figures, as shapes rather than values. */
  readonly tiles: readonly { readonly label: string; readonly value: string }[];
  readonly columns: readonly SketchColumn[];
  readonly rows: readonly (readonly string[])[];
  /** What is genuinely blocking it, said plainly. */
  readonly blocker?: string;
}

export const SKETCHES: Readonly<Record<string, Sketch>> = {
  "preview:purchasing": {
    title: "Faktury od dodavatelů",
    blurb:
      "Objednávka, příjemka a faktura vedle sebe. Schvaluje se po dokladu, účtuje se po dávce.",
    module: "M10",
    tiles: [
      { label: "Ke schválení", value: "—" },
      { label: "Nesouhlasí trojice", value: "—" },
      { label: "Za měsíc", value: "— Kč" },
    ],
    columns: [{ label: "Dodavatel" }, { label: "Doklad" }, { label: "Částka", align: "end" }, { label: "Stav" }],
    rows: [
      ["—", "—", "—", "—"],
      ["—", "—", "—", "—"],
    ],
    blocker:
      "Rozpoznávání faktur dělá DocuMe — rozhodnutím, aby se OCR nestavělo dvakrát. Chybí tam API: žádné klíče, žádný příjem dat, žádné webhooky.",
  },

  "preview:rota": {
    title: "Rozpisy směn",
    blurb:
      "Plánování proti předpovědi tržeb, docházka, hlídání zákoníku práce a rozdělení spropitného.",
    module: "M17",
    tiles: [
      { label: "Mzdové náklady", value: "— %" },
      { label: "Naplánováno hodin", value: "—" },
      { label: "Odpracováno", value: "—" },
    ],
    columns: [{ label: "Člověk" }, { label: "Po" }, { label: "Út" }, { label: "St" }, { label: "Čt" }, { label: "Pá" }],
    rows: [
      ["—", "—", "—", "—", "—", "—"],
      ["—", "—", "—", "—", "—", "—"],
    ],
  },

  "preview:analytics": {
    title: "Analytika",
    blurb:
      "Sémantická vrstva nejdřív, aby každé číslo mělo jednu definici — a teprve pak grafy nad ní.",
    module: "M18",
    tiles: [
      { label: "Tržba vs. minulý týden", value: "— %" },
      { label: "Nejziskovější položka", value: "—" },
      { label: "Předpověď na zítřek", value: "— Kč" },
    ],
    columns: [{ label: "Metrika" }, { label: "Tento týden", align: "end" }, { label: "Minulý", align: "end" }],
    rows: [
      ["—", "—", "—"],
      ["—", "—", "—"],
    ],
  },

  "preview:loyalty": {
    title: "Věrnost",
    blurb:
      "Body jako nezměnitelná kniha šarží. Načítají se z částky bez DPH a čerpají se od nejdřív propadajících.",
    module: "M15",
    tiles: [
      { label: "Aktivní hosté", value: "—" },
      { label: "Nevyčerpané body", value: "—" },
      { label: "Závazek", value: "— Kč" },
    ],
    columns: [{ label: "Host" }, { label: "Body", align: "end" }, { label: "Propadne", align: "end" }],
    rows: [
      ["—", "—", "—"],
      ["—", "—", "—"],
    ],
    blocker:
      "Modul je hotový a otestovaný — chybí mu jen místo, kam ukládat hosty a body. To přijde s hostovskými obrazovkami.",
  },
};
